import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Award, HeartHandshake, Sparkles, Shield, Stethoscope, Leaf,
  Users, PhoneCall, Activity, Plus, Minus, Building2, Landmark, Globe,
  Briefcase, MessageSquare,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";

const ICONS = {
  award: Award, "heart-handshake": HeartHandshake, sparkles: Sparkles, shield: Shield,
  stethoscope: Stethoscope, leaf: Leaf, users: Users, "phone-call": PhoneCall,
  activity: Activity, "building-2": Building2, landmark: Landmark, globe: Globe,
  briefcase: Briefcase,
};

function H1({ children }) {
  return <h1 className="mt-5 font-display font-bold text-4xl md:text-6xl text-navy-800 tracking-tight max-w-3xl">{children}</h1>;
}
function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
      <span className="h-px w-6 bg-teal" />{children}
    </span>
  );
}

export function WhyChooseUs() {
  const { data } = useFetch("/why-cards");
  return (
    <>
      <SEO title="Why choose IMG | Infinity Medical Group" description="The reasons leading individuals and organisations partner with Infinity Medical Group for integrated healthcare." />
      <Breadcrumbs />
      <section className="bg-white pt-4 pb-12"><div className="container"><Eyebrow>Why IMG</Eyebrow><H1>Trusted by patients who value precision and personal care.</H1>
        <p className="mt-6 max-w-2xl text-lg text-navy-700/75">Eight reasons leading patients and organisations choose Infinity Medical Group.</p>
      </div></section>
      <section className="bg-soft-gray py-16 lg:py-24"><div className="container">
        <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {(data || []).map((c, i) => {
            const Icon = ICONS[c.icon] || Award;
            return (
              <Item key={c.title}>
                <div className="p-7 rounded-2xl bg-white border border-navy-50 h-full" data-testid={`why-card-${i}`}>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 text-teal"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-display font-semibold text-lg text-navy-800">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy-700/75">{c.body}</p>
                </div>
              </Item>
            );
          })}
        </Stagger>
      </div></section>
    </>
  );
}

export function Industries() {
  const { data } = useFetch("/industries");
  return (
    <>
      <SEO title="Industries we serve | Infinity Medical Group" description="Sectors and organisations that partner with IMG for integrated healthcare." />
      <Breadcrumbs />
      <section className="bg-white pt-4 pb-12"><div className="container"><Eyebrow>Who we serve</Eyebrow><H1>Industries and organisations across Somalia and the region.</H1></div></section>
      <section className="bg-soft-gray py-16 lg:py-24"><div className="container">
        {(data || []).length === 0 ? (
          <div className="p-10 rounded-3xl bg-white border border-navy-50 text-center max-w-xl mx-auto">
            <p className="text-navy-700/80">Get in touch to discuss healthcare solutions for your sector.</p>
            <Link to="/contact" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">Contact us <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {data.map((it) => {
              const Icon = ICONS[it.icon] || Building2;
              return (
                <Item key={it.slug}>
                  <div className="p-7 rounded-2xl bg-white border border-navy-50 h-full" data-testid={`industry-${it.slug}`}>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-5 font-display font-semibold text-lg text-navy-800">{it.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-navy-700/75">{it.description}</p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        )}
      </div></section>
    </>
  );
}

export function ClientsPartners() {
  const { data } = useFetch("/partners");
  return (
    <>
      <SEO title="Clients & Partners | Infinity Medical Group" description="Organisations IMG partners with across healthcare, government and the private sector." />
      <Breadcrumbs />
      <section className="bg-white pt-4 pb-12"><div className="container"><Eyebrow>Clients & partners</Eyebrow><H1>The organisations we work with.</H1></div></section>
      <section className="bg-soft-gray py-16 lg:py-24"><div className="container">
        {(data || []).length === 0 ? (
          <div data-testid="partners-empty" className="p-10 rounded-3xl bg-white border border-navy-50 text-center max-w-xl mx-auto">
            <p className="text-navy-700/80">Our partner logos will be added by our team. Get in touch to discuss partnerships.</p>
            <Link to="/contact?subject=Corporate+%2F+partnership" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">Partner with us <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-4">
            {data.map((p) => (
              <div key={p.name} className="aspect-[3/2] rounded-2xl bg-white border border-navy-50 flex items-center justify-center p-6">
                {p.logo_url ? <img src={p.logo_url} alt={p.name} className="max-h-full max-w-full" /> : <span className="font-display text-navy-800">{p.name}</span>}
              </div>
            ))}
          </div>
        )}
      </div></section>
    </>
  );
}

export function Certifications() {
  const { data } = useFetch("/certifications");
  return (
    <>
      <SEO title="Certifications | Infinity Medical Group" description="Certifications and accreditations held by Infinity Medical Group." />
      <Breadcrumbs />
      <section className="bg-white pt-4 pb-12"><div className="container"><Eyebrow>Certifications</Eyebrow><H1>Our accreditations and standards.</H1></div></section>
      <section className="bg-soft-gray py-16 lg:py-24"><div className="container">
        {(data || []).length === 0 ? (
          <div className="p-10 rounded-3xl bg-white border border-navy-50 text-center max-w-xl mx-auto">
            <p className="text-navy-700/80">Certifications will be added as they are formally issued and verified.</p>
            <Link to="/contact" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">Contact us <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-3">
            {data.map((c) => (
              <div key={c.name} className="p-6 rounded-2xl bg-white border border-navy-50">
                <h3 className="font-display font-semibold text-lg text-navy-800">{c.name}</h3>
                <p className="mt-2 text-sm text-navy-700/75">{c.description}</p>
              </div>
            ))}
          </div>
        )}
      </div></section>
    </>
  );
}

export function Faq() {
  const { data } = useFetch("/faqs");
  const [open, setOpen] = useState(null);

  // Note: skip jsonLd while data is loading to avoid Helmet+StrictMode
  // dangerouslySetInnerHTML reconciliation conflicts on transition.
  const jsonLd = data && data.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: (f.answer || "").replace(/<[^>]+>/g, "") },
    })),
  } : undefined;

  return (
    <>
      <SEO title="FAQ | Infinity Medical Group" description="Answers to common questions about IMG's medical, diagnostic, and pharmacy services." jsonLd={jsonLd} />
      <Breadcrumbs />
      <section className="bg-white pt-4 pb-12"><div className="container"><Eyebrow>Frequently asked questions</Eyebrow><H1>Quick answers to the questions we hear most.</H1></div></section>
      <section className="bg-soft-gray py-16 lg:py-20"><div className="container max-w-3xl">
        <div className="divide-y divide-navy-100 rounded-3xl bg-white border border-navy-50">
          {(data || []).map((f, i) => {
            const isOpen = open === i;
            const key = f.id || f.question || `faq-${i}`;
            return (
              <div key={key} data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpen(isOpen ? null : i)} className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 hover:bg-soft-gray/50 transition-colors">
                  <div>
                    <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-teal">{f.category}</span>
                    <h3 className="mt-1 font-display font-semibold text-lg text-navy-800">{f.question}</h3>
                  </div>
                  <span className="mt-1 shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-navy-50 text-navy">{isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}</span>
                </button>
                <div
                  className={`px-6 pb-6 prose prose-navy max-w-none text-navy-700/85 ${isOpen ? "" : "hidden"}`}
                  data-testid={`faq-answer-${i}`}
                  aria-hidden={!isOpen}
                >
                  {/* FAQ answers render hand-split paragraphs.
                      We deliberately avoid <RichHtml> here: rendering 12
                      RichHtml instances inside this list triggers a React 18
                      reconciler bug with react-helmet-async. Seeded answers
                      are plain text; HTML edited via the admin TipTap editor
                      is sanitised server-side and the text content is
                      preserved here under the prose wrapper. */}
                  {(f.answer || "")
                    .replace(/<\/(p|h[2-4]|div|li)>/gi, "\n")
                    .replace(/<[^>]+>/g, "")
                    .replace(/&amp;/g, "&")
                    .replace(/&lt;/g, "<")
                    .replace(/&gt;/g, ">")
                    .replace(/&quot;/g, '"')
                    .split(/\n+/).filter(Boolean).map((para, k) => (
                      <p key={k}>{para}</p>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      </div></section>
    </>
  );
}

export function News() {
  const { data } = useFetch("/posts");
  return (
    <>
      <SEO title="News & Insights | Infinity Medical Group" description="News, insights and updates from across the Infinity Medical Group network." />
      <Breadcrumbs />
      <section className="bg-white pt-4 pb-12"><div className="container"><Eyebrow>News & Insights</Eyebrow><H1>Updates from across the IMG network.</H1></div></section>
      <section className="bg-soft-gray py-16 lg:py-24"><div className="container">
        {(data || []).length === 0 ? (
          <div className="p-10 rounded-3xl bg-white border border-navy-50 text-center max-w-xl mx-auto">
            <MessageSquare className="h-8 w-8 mx-auto text-teal" />
            <p className="mt-4 text-navy-700/80">Our first article is in the works. Check back soon, or contact our team for media enquiries.</p>
            <Link to="/contact?subject=Media+enquiry" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">Media enquiry <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {data.map((p) => (
              <article key={p.slug} className="p-6 rounded-2xl bg-white border border-navy-50">
                <h3 className="font-display font-semibold text-lg text-navy-800">{p.title}</h3>
                <p className="mt-3 text-sm text-navy-700/75">{p.excerpt}</p>
              </article>
            ))}
          </div>
        )}
      </div></section>
    </>
  );
}
